import { Injectable } from '@angular/core';
import {
	ComponentController,
	MessagingService,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest } from 'rxjs';
import {
	finalize,
	first,
	map,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs/operators';

import { environment } from 'environments/environment';
import { UserAvailabilityType } from 'user/model/user-availability.type';
import { UserApplicationPort } from 'user/port/user-application.port';

import { SpreengUtils } from '../../../../common/spreeng-utils';
import {
	CoachSettingsCalendarDay,
	CoachSettingsCalendarSlot,
} from './coach-settings-calendar.model';
import {
	CoachSettingsCalendarState,
	CoachSettingsCalendarStore,
} from './coach-settings-calendar.store';

@Injectable()
@UntilDestroy()
export class CoachSettingsCalendarController extends ComponentController<CoachSettingsCalendarState> {
	public isSaving$: Observable<boolean>;
	public availabilityType$: Observable<UserAvailabilityType>;
	public calendarDays$: Observable<CoachSettingsCalendarDay[]>;

	constructor(
		store: CoachSettingsCalendarStore,
		private _messagingService: MessagingService,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort
	) {
		super(store);

		this._initObservables();
		this._initListeners();
	}

	public toggleDaySlot(slot: CoachSettingsCalendarSlot) {
		this.store.update((state) => {
			const availabilityIndex = state.availabilities.findIndex(
				(availability) =>
					availability.time.hours === slot.time.hours &&
					availability.time.minutes === slot.time.minutes &&
					availability.dayIndex === slot.dayIndex
			);
			const availabilities = [...state.availabilities];

			if (availabilityIndex >= 0) {
				availabilities.splice(availabilityIndex, 1);
			} else {
				availabilities.push({ ...slot, type: state.availabilityType });
			}

			return { availabilities };
		});
	}

	public updateAvailabilities(): Promise<void> {
		this.store.update({ isSaving: true });

		return this._userApplicationPort
			.observeSessionUserId()
			.pipe(
				switchMap((userId) =>
					this._userApplicationPort.updateUserAvailabilities({
						userId,
						availabilities: this.getValue().availabilities,
					})
				),
				finalize(() => this.store.update({ isSaving: false })),
				tap(() =>
					this._messagingService.publishMessage({
						summaryKey: 'presentation.app.page.settings.coach.calendar.saved.summary',
						detailKey: 'presentation.app.page.settings.coach.calendar.saved.detail',
						severity: 'success',
					})
				),
				first()
			)
			.toPromise();
	}

	public updateAvailabilityType(availabilityType: UserAvailabilityType) {
		this.store.update({ availabilityType });
	}

	private _initListeners() {
		this._userApplicationPort
			.observeSessionUser()
			.pipe(
				map((user) => user?.availabilities ?? []),
				tap((availabilities) =>
					this.store.update({
						availabilities: availabilities.map(
							(availability) =>
								({
									time: SpreengUtils.minutesToTime(availability.startOffset),
									startOffset: availability.startOffset,
									endOffset: availability.endOffset,
									dayIndex: availability.dayIndex,
									type: availability.type,
									id: availability.id,
								} as CoachSettingsCalendarSlot)
						),
					})
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initObservables() {
		this.calendarDays$ = combineLatest([
			this._translateService.stream('presentation.primeng.calendar'),
			this.select('availabilities'),
		]).pipe(
			map(([calendar, availabilities]) =>
				(calendar.dayNames || []).map((dayName, index) => {
					const dayIndex = (index + calendar.firstDayOfWeek) % 7;

					const dayStartOffset = SpreengUtils.timeToMinutes(
						environment.calendarStartTime
					);
					const dayEndOffset = SpreengUtils.timeToMinutes(
						environment.calendarEndTime
					);
					const slotInterval = SpreengUtils.timeToMinutes(
						environment.calendarInterval
					);

					const slots: CoachSettingsCalendarSlot[] = [];
					let currentOffset = dayStartOffset;
					while (currentOffset <= dayEndOffset) {
						const time = SpreengUtils.minutesToTime(currentOffset);

						const slot = availabilities.find(
							(availability) =>
								currentOffset >= availability.startOffset &&
								currentOffset < availability.endOffset &&
								availability.dayIndex === dayIndex
						);

						slots.push({
							type: slot?.type ?? UserAvailabilityType.NONE,
							endOffset: currentOffset + slotInterval,
							startOffset: currentOffset,
							dayIndex,
							time,
							id: slot?.id,
						});

						currentOffset = currentOffset + slotInterval;
					}

					return {
						name: calendar.dayNames[dayIndex],
						index: dayIndex,
						slots,
					} as CoachSettingsCalendarDay;
				})
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.availabilityType$ = this.select('availabilityType');

		this.isSaving$ = this.select('isSaving');
	}
}
